// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Obj {[key: string]: any}

/**
 * Find the differences between two objects and push to a new object
 * Modified from: (c) 2019 Chris Ferdinandi & Jascha Brinkmann, MIT License, https://gomakethings.com & https://twitter.com/jaschaio
 *
 * Takes a parameter of the shape:
 * {
 *   obj1 {Object}: The original object
 *   obj2 {Object}: The object to compare against it
 *   limitToObj1Keys? {Boolean} (optional) only include keys from Obj1. Defaults to FALSE.
 *   includeCheckForMissingKeys?: {Boolean} (optional) Will output the key with null value if missing in ObjB. Defaults to TRUE.
 * }
 *
 * examples:
 *
 * getObjectDiff({
 *  obj1: { a: 1, b: 2 },
 *  obj2: { a: 2 },
 * }) // => { a: 2, b: null}
 *
 * getObjectDiff({
 *  obj1: { a: 1, b: 2 },
 *  obj2: { a: 2 },
 *  includeCheckForMissingKeys: false
 * }) // => { a: 2 }
 *
 * @param args
 */
export const getObjectDiff = (args: {
    obj1: Obj;
    obj2: Obj;
    limitToObj1Keys?: boolean;
    includeCheckForMissingKeys?: boolean;
  }): Obj => {
  const limitToObj1Keys = (args.limitToObj1Keys === undefined) ? false : args.limitToObj1Keys
  // eslint-disable-next-line max-len
  const includeCheckForMissingKeys = (args.includeCheckForMissingKeys === undefined) ? true : args.includeCheckForMissingKeys

  // Make sure an object to compare is provided
  if (!args.obj2 || Object.prototype.toString.call(args.obj2) !== '[object Object]') {
    return args.obj1
  }

  //
  // Variables
  //

  const diffs: Obj = {}

  //
  // Methods
  //

  /**
     * Check if two arrays are equal
     * @param  {Array}   arr1 The first array
     * @param  {Array}   arr2 The second array
     * @return {Boolean}      If true, both arrays are equal
     */
  const arraysMatch = (arr1: unknown[], arr2: unknown[]) => {
    // Check if the arrays are the same length
    if (arr1.length !== arr2.length) return false

    // Check if all items exist and are in the same order
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false
    }

    // Otherwise, return true
    return true
  }

  /**
     * Compare two items and push non-matches to object
     * @param  {*}      item1 The first item
     * @param  {*}      item2 The second item
     * @param  {String} key   The key in our object
     */
  const compare = (item1: unknown, item2: unknown, theKey: string) => {
    // Get the object type
    const type1 = Object.prototype.toString.call(item1)
    const type2 = Object.prototype.toString.call(item2)

    // If type2 is undefined it has been removed
    if (type2 === '[object Undefined]' && includeCheckForMissingKeys) {
      diffs[theKey] = null
      return
    }

    // If items are different types
    if (type1 !== type2) {
      diffs[theKey] = item2
      return
    }

    // If an object, compare recursively
    if (type1 === '[object Object]') {
      // console.log('recursve')
      const objDiff = getObjectDiff({
        obj1: item1 as Obj,
        obj2: item2 as Obj,
        limitToObj1Keys,
        includeCheckForMissingKeys,
      })
      if (Object.keys(objDiff).length > 0) {
        diffs[theKey] = objDiff
      }
      return
    }

    // If an array, compare
    if (type1 === '[object Array]') {
      if (!arraysMatch(item1 as unknown[], item2 as unknown[])) {
        diffs[theKey] = item2
      }
      return
    }

    // Else if it's a function, convert to a string and compare
    // Otherwise, just compare
    if (type1 === '[object Function]') {
      if ((item1 as Obj).toString() !== (item2 as Obj).toString()) {
        diffs[theKey] = item2
      }
    } else if (item1 !== item2) {
      diffs[theKey] = item2
    }
  }

  //
  // Compare our objects
  //

  // Loop through the first object
  Object.keys(args.obj1).forEach((k) => {
    compare(args.obj1[k], args.obj2[k], k)
  })

  // Loop through the second object and find missing items
  if (!limitToObj1Keys) {
    Object.keys(args.obj2).forEach((k) => {
      if (!args.obj1[k] && args.obj1[k] !== args.obj2[k]) {
        diffs[k] = args.obj2[k]
      }
    })
  }

  // Return the object of differences
  return diffs
}
